import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {ImageSource, type ImageInput} from "@khan/guide-types";

import {ImageAttachModal} from "./image-attach-modal.tsx";
import {CameraCaptureModalContent} from "./modal-content/camera-capture-modal-content.tsx";
import {FileUploadModalContent} from "./modal-content/file-upload-modal-content.tsx";
import {UrlModalContent} from "./modal-content/url-modal-content.tsx";
import {convertImageToDataURL} from "./utils/convert-image-to-data-url.ts";
import {createImageInput} from "./utils/create-image-input.ts";

interface Props {
    // Function to call when the modal should be closed.
    onCloseModal: () => void;
    // Optional callback invoked with the attached images.
    onAttachImages?: (images: ImageInput[]) => void;
    // Which image input experience to show (camera, file, or URL).
    imageSource?: ImageSource;
}

/**
 * Manages the state and logic for the image attachment modal experience.
 * It determines which image input experience to show (camera, file upload, or URL),
 * manages all intermediate state, and passes the correct props to the presentational
 * ImageAttachModal component.
 */
export const ImageAttachModalWrapper = ({
    onCloseModal,
    onAttachImages,
    imageSource = ImageSource.ProvidedByFile,
}: Props) => {
    const imageInputMenuIsOn = useFeatureIsOn("image-input-menu");
    const [overTarget, setOverTarget] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [image, setImage] = useState<ImageInput>();
    const [imageURL, setImageURL] = useState<string>("");
    const [pendingImageURL, setPendingImageURL] = useState<string>("");
    const [selectedFilename, setSelectedFilename] = useState<string | null>(
        null,
    );
    const [draggedFile, setDraggedFile] = useState<File | undefined>(undefined);
    const [isLoadingURL, setIsLoadingURL] = useState<boolean>(false);

    const clearImage = () => {
        setImage(undefined);
        setSelectedFilename(null);
    };

    const clearImageURL = () => {
        setImageURL("");
        setPendingImageURL("");
    };

    const handleSetImage = (
        dataURL: string,
        source: ImageInput["source"],
        sourceURL?: string,
    ) => {
        const imageInput = createImageInput(dataURL, source, sourceURL);
        setImage(imageInput);
    };

    const handleSetImageURL = () => {
        setError(null);
        clearImage();
        setIsLoadingURL(true);
        handleConvertImageURLToDataURL(pendingImageURL);
    };

    const handleConvertImageURLToDataURL = async (url: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const img = new Image();
            img.onload = () => {
                try {
                    const scaledDataURL = convertImageToDataURL(img);
                    setImageURL(url);
                    setIsLoadingURL(false);
                    handleSetImage(
                        scaledDataURL,
                        ImageSource.ProvidedByURL,
                        url,
                    );
                } catch (error) {
                    setError(
                        t`We couldn't process that image.  Please try again, or try a different image.`,
                    );
                    handleClearImage();
                }
            };
            img.onerror = () => {
                setIsLoadingURL(false);
                setError(
                    t`We couldn't load that image URL.  Please try again, or try downloading the image and attaching it as a file.`,
                );
                handleClearImage();
            };
            img.src = URL.createObjectURL(blob);
        } catch (error) {
            setError(
                t`We couldn't fetch that image URL.  Please try again, or try a different URL`,
            );
            handleClearImage();
            setIsLoadingURL(false);
        }
    };

    const handleConfirmAttachment = () => {
        if (!image) {
            throw new Error("No image to attach");
        }
        onAttachImages?.([image]);
        onCloseModal();
    };

    // Removes the selected image so the user can try a different image
    const handleClearImage = () => {
        clearImage();
        clearImageURL();
        setDraggedFile(undefined);
        setIsLoadingURL(false);
    };

    const handleCancel = () => {
        onCloseModal();
    };

    let content: React.ReactNode = null;
    switch (imageSource) {
        case ImageSource.ProvidedByCamera:
            content = (
                <CameraCaptureModalContent image={image} setImage={setImage} />
            );
            break;
        case ImageSource.ProvidedByURL:
            content = (
                <UrlModalContent
                    image={image}
                    imageURL={imageURL}
                    error={error}
                    pendingImageURL={pendingImageURL}
                    setPendingImageURL={setPendingImageURL}
                    handleSetImageURL={handleSetImageURL}
                    handleClearImage={handleClearImage}
                    selectedFilename={selectedFilename}
                    loading={isLoadingURL}
                />
            );
            break;
        case ImageSource.ProvidedByFile:
        default:
            content = (
                <FileUploadModalContent
                    image={image}
                    overTarget={overTarget}
                    setOverTarget={setOverTarget}
                    handleSetImage={handleSetImage}
                    handleClearImage={handleClearImage}
                    selectedFilename={selectedFilename}
                    setSelectedFilename={setSelectedFilename}
                    imageURL={imageURL}
                    draggedFile={draggedFile}
                    setDraggedFile={setDraggedFile}
                />
            );
            break;
    }

    // TODO: (KORE-1313) Remove this when the image input menu is fully launched.
    const fileTitle = imageInputMenuIsOn
        ? t`Attach image from file`
        : t`Attach Image File`;

    const title =
        imageSource === ImageSource.ProvidedByCamera
            ? t`Attach image from camera`
            : imageSource === ImageSource.ProvidedByFile
              ? fileTitle
              : t`Attach image from URL`;

    const footer = (
        <View style={styles.footer}>
            <Button
                kind="tertiary"
                onClick={handleCancel}
                aria-label={t`Cancel image attach`}
            >
                {t`Cancel`}
            </Button>
            <Button
                kind="primary"
                disabled={!image}
                onClick={handleConfirmAttachment}
                testId="modal-attach-image-button"
            >
                {t`Attach image`}
            </Button>
        </View>
    );

    const warningMessage =
        imageSource === ImageSource.ProvidedByURL
            ? t`Please avoid linking to images that contain personal or sensitive information.`
            : imageSource === ImageSource.ProvidedByCamera
              ? t`When capturing images, ensure they don't include any sensitive information.`
              : t`Please avoid attaching images that contain personal or sensitive information.`;

    return (
        <ImageAttachModal
            title={title}
            content={
                <View style={styles.modalContent}>
                    <LabelMedium>{warningMessage}</LabelMedium>
                    <Strut size={16} />
                    {content}
                </View>
            }
            footer={footer}
        />
    );
};

const styles = StyleSheet.create({
    modalContent: {
        alignItems: "center",
        justifyContent: "flex-start",
    },
    footer: {
        width: "100%",
        flexDirection: "row",
        gap: spacing.small_12,
        justifyContent: "right",
    },
});
